import { getErrorMessage } from "api/errors";
import {
  hasAnyAccessToResource,
  hasViewAccessToResource,
  useSettings,
} from "api/general";
import { useAppDispatch } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { useStyles } from "components/Layout/GlobalManagement/Settings/Settings.styles";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { clearTablePage } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { ADMIN_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListResponse, ListingActions } from "types/listing";
import { links } from "utilities/links";
import { handleBooleanValue } from "utilities/strings";

function CreateItems(items: any[]): Row[] {
  const { message } = useMessage();
  return items.map((item) => ({
    cells: [
      { content: item.firstName, property: "firstName", dataHjSuppress: true },
      { content: item.lastName, property: "lastName", dataHjSuppress: true },
      { content: item.email, property: "email", dataHjSuppress: true },
      { content: item.roles[0]?.name, property: "role", dataHjSuppress: true },
      {
        content: message({
          id: handleBooleanValue(item.settings?.notificationsEnabled === "1"),
        }),
        property: "notificationsEnabled",
      },
    ],
    id: item.id,
    metadata: {
      disabled: !item.isActive,
      notificationsEnabled: item.settings?.notificationsEnabled === "1",
    },
  }));
}

export function AdminList() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { message } = useMessage();
  const { notify } = useNotifications();
  const { listing, updateListing } = useListing(ADMIN_LIST_ID);
  const appDispatch = useAppDispatch();
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const [filters, setFilters] = useState<SelectedFilter[]>([]);
  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      ADMIN_LIST_ID,
    );

    setFilters(filters);
  };
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const hasAnyAdminPermission = hasAnyAccessToResource("ADMIN");
  const hasViewAdminPermission = hasViewAccessToResource("ADMIN");
  const hasAnyAccesstoSystemLogs = hasAnyAccessToResource("AUDIT");
  const { useAdminsList, enableNotifications } = useSettings();

  const { data, error } = useAdminsList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "asc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "firstName",
    dynamicSearch,
  }) as { data: ListResponse; error?: any };

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, ADMIN_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, data, error]);
  const headItems = createHeadItems([
    {
      headerText: message({ id: "firstName" }),
      property: "firstName",
      isSortable: true,
    },
    {
      headerText: message({ id: "lastName" }),
      property: "lastName",
      isSortable: true,
    },
    {
      headerText: message({ id: "email" }),
      property: "email",
      isSortable: true,
    },
    {
      headerText: message({ id: "role" }),
      property: "role",
      isSortable: false,
    },
    {
      headerText: message({ id: "notificationsEnabled" }),
      property: "notificationsEnabled",
      isSortable: false,
    },
  ]);

  const items = data?.items ? CreateItems(data.items) : [];

  return (
    <>
      <Subheader>
        <FiltersV2
          data-test-id="admins-list"
          donutLabel={message({ id: "admins" })}
          properties={[
            {
              query: "id",
              label: message({ id: "id" }),
              type: "text",
            },
            {
              query: "email",
              label: message({ id: "email" }),
              type: "text",
            },
            {
              query: "phone",
              label: message({ id: "phone" }),
              type: "text",
            },
            {
              query: "firstName",
              label: message({ id: "firstName" }),
              type: "text",
            },
            {
              query: "lastName",
              label: message({ id: "lastName" }),
              type: "text",
            },
            {
              query: "isActive",
              label: message({ id: "isActive" }),
              type: "boolean-status",
            },
            {
              query: "role",
              label: message({ id: "role" }),
              type: "text",
            },
            {
              query: "createdAt",
              label: message({ id: "createdOn" }),
              type: "datetime",
            },
          ]}
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
          total={data?.total}
        />
        <DynamicSearch
          property="firstName"
          properties={[
            { id: "email", label: "email" },
            { id: "phone", label: "phone" },
            { id: "firstName", label: "firstName" },
            { id: "lastName", label: "lastName" },
            { id: "role", label: "role" },
          ]}
          setDynamicSearch={setDynamicSearch}
          onSearch={() =>
            appDispatch(clearTablePage({ tableName: ADMIN_LIST_ID }))
          }
        />
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={ADMIN_LIST_ID}
        data-test-id="admin-list"
        onRowClick={(id: string) => {
          navigate(links.editAdmin(id as string));
        }}
        sort={{
          order: listing.order || "asc",
          property: listing.property || "firstName",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        actions={{
          body: [
            {
              action: async (id) => {
                try {
                  await enableNotifications(id as string, "0");
                  notify(message({ id: "adminSettingsEdited" }));
                } catch (exception) {
                  notify(getErrorMessage(exception));
                }
              },
              disableCondition: () => hasViewAdminPermission,
              hideCondition: (item) =>
                !item.metadata?.notificationsEnabled || !hasAnyAdminPermission,
              label: message({ id: "disableNotifications" }),
            },
            {
              action: (id) => navigate(links.editAdmin(id as string)),
              label: message({ id: "edit" }),
            },
            {
              action: async (id) => {
                try {
                  await enableNotifications(id as string, "1");
                  notify(message({ id: "adminSettingsEdited" }));
                } catch (exception) {
                  notify(getErrorMessage(exception));
                }
              },
              disableCondition: () => hasViewAdminPermission,
              hideCondition: (item) =>
                item.metadata?.notificationsEnabled || !hasAnyAdminPermission,
              label: message({ id: "enableNotifications" }),
            },
          ],
        }}
        columns={data && headItems}
        rows={data && items}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          page: listing.page,
          onChangePage: (page) => {
            updateListing({
              payload: page,
              type: ListingActions.CHANGE_PAGE,
            });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            });
          },
          rowsPerPage: listing.rowsPerPage,
        }}
      />
      {hasAnyAccesstoSystemLogs && (
        <Link to={links.systemLogs} className={classes.systemLogsLink}>
          {message({ id: "systemLogs" })}
        </Link>
      )}
    </>
  );
}
